/**
 * Process Section Component
 *
 * React implementation of the process section renderer.
 * Supports variants: steps, timeline, cards
 */

import { ProcessData, SectionProps, EditableElement as EditableElementType } from '../types';
import { EditableText, EditableImage } from '../components/EditableElement';

// =============================================================================
// TYPES
// =============================================================================

interface ProcessProps extends SectionProps<ProcessData> {}

// =============================================================================
// HELPER: Find element by path
// =============================================================================

function findElement(elements: EditableElementType[] | undefined, path: string): EditableElementType | undefined {
  return elements?.find(el => el.path === path);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Process({ data, ctx, sectionId, elements }: ProcessProps) {
  const variant = data.variant || 'steps';

  switch (variant) {
    case 'timeline':
      return <ProcessTimeline data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'cards':
      return <ProcessCards data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    default:
      return <ProcessSteps data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
  }
}

// =============================================================================
// STEPS VARIANT
// =============================================================================

function ProcessSteps({ data, ctx, elements }: ProcessProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="process"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg max-w-2xl mx-auto"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg max-w-2xl mx-auto" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connecting line */}
          <div
            className="hidden lg:block absolute top-12 left-0 right-0 h-0.5"
            style={{ backgroundColor: `${recipe.colors.primary}30` }}
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {data.steps?.map((step, i) => {
              const titleEl = findElement(elements, `steps.${i}.title`);
              const descEl = findElement(elements, `steps.${i}.description`);

              return (
                <div key={i} className="relative text-center">
                  {/* Step Number */}
                  <div
                    className="w-12 h-12 mx-auto mb-6 rounded-full flex items-center justify-center text-xl font-bold text-white relative z-10"
                    style={{ backgroundColor: recipe.colors.primary }}
                  >
                    {step.number || i + 1}
                  </div>

                  {/* Icon */}
                  {step.icon && (
                    <div className="text-3xl mb-4">{step.icon}</div>
                  )}

                  {/* Title */}
                  {titleEl ? (
                    <EditableText
                      element={titleEl}
                      as="h3"
                      className="text-xl font-semibold mb-3"
                      style={{ color: recipe.colors.text }}
                    />
                  ) : (
                    <h3 className="text-xl font-semibold mb-3" style={{ color: recipe.colors.text }}>
                      {step.title}
                    </h3>
                  )}

                  {/* Description */}
                  {descEl ? (
                    <EditableText
                      element={descEl}
                      as="p"
                      style={{ color: recipe.colors.textMuted }}
                    />
                  ) : (
                    <p style={{ color: recipe.colors.textMuted }}>{step.description}</p>
                  )}

                  {/* Duration */}
                  {step.duration && (
                    <p className="text-sm mt-3" style={{ color: recipe.colors.primary }}>
                      {step.duration}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        {data.cta && (
          <div className="text-center mt-12">
            <a
              href={data.cta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg text-white"
              style={{
                backgroundColor: recipe.colors.primary,
                borderRadius: recipe.borders.buttonRadius,
              }}
            >
              {data.cta.text}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// TIMELINE VARIANT
// =============================================================================

function ProcessTimeline({ data, ctx, elements }: ProcessProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');

  return (
    <section
      id="process"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.surface,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div
            className="absolute left-8 md:left-1/2 md:-translate-x-0.5 top-0 bottom-0 w-0.5"
            style={{ backgroundColor: `${recipe.colors.primary}30` }}
          />

          <div className="space-y-12">
            {data.steps?.map((step, i) => {
              const titleEl = findElement(elements, `steps.${i}.title`);
              const descEl = findElement(elements, `steps.${i}.description`);
              const isLeft = i % 2 === 0;

              return (
                <div
                  key={i}
                  className={`relative flex items-start gap-8 ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Content */}
                  <div className={`flex-1 pl-20 md:pl-0 ${isLeft ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    {step.duration && (
                      <p className="text-sm mb-2" style={{ color: recipe.colors.primary }}>
                        {step.duration}
                      </p>
                    )}
                    {titleEl ? (
                      <EditableText
                        element={titleEl}
                        as="h3"
                        className="text-xl font-semibold mb-2"
                        style={{ color: recipe.colors.text }}
                      />
                    ) : (
                      <h3 className="text-xl font-semibold mb-2" style={{ color: recipe.colors.text }}>
                        {step.title}
                      </h3>
                    )}
                    {descEl ? (
                      <EditableText
                        element={descEl}
                        as="p"
                        style={{ color: recipe.colors.textMuted }}
                      />
                    ) : (
                      <p style={{ color: recipe.colors.textMuted }}>{step.description}</p>
                    )}
                    {step.items && step.items.length > 0 && (
                      <ul className={`mt-3 space-y-1 ${isLeft ? 'md:text-right' : ''}`}>
                        {step.items.map((item, ii) => (
                          <li key={ii} className="text-sm" style={{ color: recipe.colors.textMuted }}>
                            • {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Circle */}
                  <div
                    className="absolute left-6 md:left-1/2 md:-translate-x-1/2 w-4 h-4 rounded-full border-4"
                    style={{
                      backgroundColor: recipe.colors.background,
                      borderColor: recipe.colors.primary,
                    }}
                  />

                  {/* Empty space for alignment */}
                  <div className="hidden md:block flex-1" />
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        {data.cta && (
          <div className="text-center mt-12">
            <a
              href={data.cta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg text-white"
              style={{
                backgroundColor: recipe.colors.primary,
                borderRadius: recipe.borders.buttonRadius,
              }}
            >
              {data.cta.text}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// CARDS VARIANT
// =============================================================================

function ProcessCards({ data, ctx, elements }: ProcessProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="process"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg max-w-2xl mx-auto"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg max-w-2xl mx-auto" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.steps?.map((step, i) => {
            const titleEl = findElement(elements, `steps.${i}.title`);
            const descEl = findElement(elements, `steps.${i}.description`);
            const imageEl = findElement(elements, `steps.${i}.image`);

            return (
              <div
                key={i}
                className="rounded-xl overflow-hidden"
                style={{
                  backgroundColor: recipe.colors.surface,
                  borderRadius: recipe.borders.cardRadius,
                  boxShadow: recipe.shadows.card,
                }}
              >
                {/* Image */}
                {(step.image || imageEl) && (
                  <div className="aspect-video">
                    {imageEl ? (
                      <EditableImage
                        element={imageEl}
                        alt={step.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                )}

                <div className="p-6">
                  {/* Step Number */}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold text-white mb-4"
                    style={{ backgroundColor: recipe.colors.primary }}
                  >
                    {step.number || i + 1}
                  </div>

                  {/* Title */}
                  {titleEl ? (
                    <EditableText
                      element={titleEl}
                      as="h3"
                      className="text-xl font-semibold mb-3"
                      style={{ color: recipe.colors.text }}
                    />
                  ) : (
                    <h3 className="text-xl font-semibold mb-3" style={{ color: recipe.colors.text }}>
                      {step.title}
                    </h3>
                  )}

                  {/* Description */}
                  {descEl ? (
                    <EditableText
                      element={descEl}
                      as="p"
                      style={{ color: recipe.colors.textMuted }}
                    />
                  ) : (
                    <p style={{ color: recipe.colors.textMuted }}>{step.description}</p>
                  )}

                  {/* Items */}
                  {step.items && step.items.length > 0 && (
                    <ul className="mt-4 space-y-2">
                      {step.items.map((item, ii) => (
                        <li key={ii} className="flex items-start gap-2 text-sm">
                          <svg
                            className="w-5 h-5 flex-shrink-0 mt-0.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            style={{ color: recipe.colors.primary }}
                          >
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span style={{ color: recipe.colors.textMuted }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Link */}
                  {step.link && (
                    <a
                      href={step.link.url}
                      className="inline-flex items-center mt-4 font-medium"
                      style={{ color: recipe.colors.primary }}
                    >
                      {step.link.text}
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        {data.cta && (
          <div className="text-center mt-12">
            <a
              href={data.cta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg text-white"
              style={{
                backgroundColor: recipe.colors.primary,
                borderRadius: recipe.borders.buttonRadius,
              }}
            >
              {data.cta.text}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

export default Process;
